// Cookie セッション前提の API クライアント。
// 配信環境では app.<env>... と api.<env>... が同一サイトのため、credentials:'include' で
// セッション Cookie(session_token) が自動送信される。401 は未ログインとして扱う。

import { API_URL } from "./env";

/**
 * 401（セッション切れ）を受けたときに呼ばれるハンドラ。
 *
 * 各画面が個別にエラー表示すると、操作中にセッションが切れたときに画面ごとに
 * 違う見え方になる。ここで一元的に受けて、認証状態を未認証に落としログインへ
 * 誘導する（登録は AuthProvider が行う）。
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

interface RequestOptions {
  /**
   * 401 を受けても共通ハンドラを呼ばない。
   *
   * 「セッションが切れた」以外の理由で 401 が返る経路で指定する。
   *   - 起動時のセッション復元（`GET /auth`）: 未ログインの 401 は正常な経路
   *   - ログイン（`POST /auth/login`）: 資格情報の誤りであってセッション切れではない
   *
   * 指定しないと、画面側がエラーを表示する前に認証状態が未認証へ落ち、
   * ログイン画面へ飛ばされて本当の理由が利用者に見えない。
   */
  skipUnauthorizedHandler?: boolean;
}

export class ApiError extends Error {
  status: number;
  // API の構造化エラーコード（lib/enums/error_codes.py の ErrorCode）。
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * 例外を画面に出す文言にする。
 *
 * API のエラーは `{"detail": str}` で統一されており、そのまま利用者に見せて
 * 意味が通る文言になっている（[[exceptions]]）。それ以外（通信断など）は
 * 呼び出し側が渡す fallback を使う。
 */
export function errorMessage(e: unknown, fallback = "処理に失敗しました。"): string {
  return e instanceof ApiError ? e.message : fallback;
}

async function request<T>(
  path: string,
  init?: RequestInit,
  options?: RequestOptions,
): Promise<T> {
  // 未設定を無言で同一オリジンへフォールバックさせず、明確に失敗させる。
  if (!API_URL) {
    throw new ApiError(0, "VITE_API_URL が未設定です");
  }
  // Content-Type はボディがあるときだけ付ける（bodyless GET に付けると不要な
  // CORS プリフライトを誘発するため）。FormData のときは**付けない**。
  // 手で付けると multipart の boundary が入らず、サーバ側で解釈できなくなる。
  const headers: Record<string, string> = {
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (init?.body != null && !(init.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (!res.ok) {
    if (res.status === 401 && !options?.skipUnauthorizedHandler) {
      onUnauthorized?.();
    }
    let message = res.statusText;
    let code: string | undefined;
    try {
      // API のエラーレスポンスは {"detail": str, "code": str} で統一されている。
      const body = await res.json();
      message = body.detail ?? body.message ?? message;
      code = body.code;
    } catch {
      // JSON でない/空ボディはステータステキストのまま。
    }
    throw new ApiError(res.status, message, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(
      path,
      {
        method: "POST",
        body: body != null ? JSON.stringify(body) : undefined,
      },
      options,
    ),
  /** multipart/form-data を送る（ファイルのアップロード）。 */
  postForm: <T>(path: string, form: FormData, options?: RequestOptions) =>
    request<T>(path, { method: "POST", body: form }, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(
      path,
      {
        method: "PUT",
        body: body != null ? JSON.stringify(body) : undefined,
      },
      options,
    ),
  patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { method: "DELETE" }, options),
  /**
   * ファイルを受け取る（JSON ではない応答）。
   *
   * ``request`` は応答を JSON として読むので、本体がファイルの経路では使えない。
   * 失敗時のエラーの形（detail / code）は同じなので、そこだけ合わせてある。
   */
  download: downloadFile,
};

/** ``Content-Disposition`` からファイル名を取る。取れなければ null。 */
function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  // RFC 5987 の filename*=UTF-8''... を優先する（日本語のファイル名はこちら）。
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // 壊れていれば素の filename= に落とす。
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : null;
}

async function downloadFile(
  path: string,
): Promise<{ blob: Blob; filename: string | null }> {
  if (!API_URL) {
    throw new ApiError(0, "API の接続先が設定されていません。");
  }
  const res = await fetch(`${API_URL}${path}`, { credentials: "include" });

  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    let message = res.statusText;
    let code: string | undefined;
    try {
      const body = await res.json();
      message = body.detail ?? body.message ?? message;
      code = body.code;
    } catch {
      // JSON でない/空ボディはステータステキストのまま。
    }
    throw new ApiError(res.status, message, code);
  }

  return {
    blob: await res.blob(),
    filename: filenameFrom(res.headers.get("Content-Disposition")),
  };
}
